import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import { DashboardLayoutComponent, PanelModel } from '@syncfusion/ej2-react-layouts';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomField } from 'common/models/Configuration/CustomFields';
import {
  CandidateRecordDashboardElementDefinition,
  CandidateRecordDashboardElementType,
} from 'common/models/Dashboard/EditLayout';
import { CustomFieldSettingsMap, CustomFieldType } from 'common/models/ScreenLayouts/CustomFields';
import useUnsavedChangesDialog from 'hooks/UseUnsavedChangesDialog';
import { GetCustomFieldsByEntity_OnlyActive } from 'services/CustomFieldsService';
import { UdfCandidateFieldMapObj } from 'util/Definitions/ScreenLayouts/CustomFields';
import RecordAvatarComponent from 'components/Dashboards/Graphs/ClientAvatarComponent';
import PanelWrapper from 'components/Dashboards/PanelWrapper';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import {
  GetCustomerSettingBySettingName,
  UpdateCustomerSettingBySettingName,
} from 'services/ConfigurationService';
import ConfirmationDialog from 'components/Dialogs/Generic/ConfirmationDialog';
import Divider from '@mui/material/Divider';
import MultiLineTextElement from 'components/ScreenLayouts/MultiLineTextElement';
import SingleFieldElement from 'components/ScreenLayouts/SingleFieldElement';
import { PlaceholderCandidate, PlaceholderCandidateWorkHistory } from 'util/Definitions/Candidates';
import {
  CandidateQuickViewLayoutSettings,
  DefaultCandidateQuickViewLayoutElements,
  GetDefaultSizeByElementType,
  GetElementTypeById,
  GetMinSizeByElementType,
  GetPanelDefinitionsFromPanelModels,
} from 'util/Definitions/QuickViewLayout/Candidate';
import ViewCandidateLocationElement from 'components/ScreenLayouts/Candidates/ViewCandidateLocationElement';
import AddCandidateDashboardElementDialog from 'components/Dialogs/Dashboard/AddCandidateDashboardElementDialog';
import CandidateWorkHistorySummaryElement from 'components/ScreenLayouts/Candidates/CandidateWorkHistorySummary';

interface Props {
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
}

const candidate = PlaceholderCandidate;

const LayoutElementsToString = (elements: PanelModel[]) => {
  if (elements.length === 0) return '';
  const s = elements.map((e) => ElementString(e));
  s.sort();
  return s.join('|');
};

const ElementString = (element: PanelModel) => {
  const type = GetElementTypeById(element.id ?? '');
  const val = `${type}|${element.row}|${element.col}|${element.sizeX}|${element.sizeY}`;
  return val;
};

const { unitWidth, unitHeight, gapX, gapY, columns } = CandidateQuickViewLayoutSettings;
const cellSpacing = [gapX, gapY];

const customerSettingName = 'CandidateQuickViewLayoutElements';

export default function QuickViewLayoutAdmin({ loadingHandler, errorHandler }: Props) {
  const [layoutSwapControl, setLayoutSwapControl] = useState(true);
  const [screenResizedControl, setScreenResizedControl] = useState(false);
  const [showAddElementDialog, setShowAddElementDialog] = useState(false);
  const [fetchingSavedState, setFetchingSavedState] = useState(false);
  const [fetchingActiveFields, setFetchingActiveFields] = useState(false);
  const [fetchedSavedState, setFetchedSavedState] = useState(false);
  const [activeFields, setActiveFields] = useState<CustomField[]>([]);
  const [initialState, setInitialState] = useState<PanelModel[] | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetToDefaultConfirmDialog, setShowResetToDefaultConfirmDialog] = useState(false);
  const [lastSavedState, setLastSavedState] = useState<PanelModel[] | null>(null);
  const layoutRef = useRef<DashboardLayoutComponent | null>(null);
  const theme = useTheme();

  const defaultElements = useMemo(() => DefaultCandidateQuickViewLayoutElements, []);

  useEffect(() => {
    const windowResizeHandler = () => setScreenResizedControl((prev) => !prev);
    window.addEventListener('resize', windowResizeHandler);
    return () => window.removeEventListener('resize', windowResizeHandler);
  }, []);

  useEffect(() => {
    const getActiveFields = async () => {
      setFetchingActiveFields(true);
      const res = await GetCustomFieldsByEntity_OnlyActive(3);
      if (res) setActiveFields(res);
      setFetchingActiveFields(false);
    };
    getActiveFields();
  }, []);

  const customFieldsSettingsMap = useMemo<CustomFieldSettingsMap>(() => {
    const obj: CustomFieldSettingsMap = {};
    activeFields.forEach((u) => {
      const customFieldNumber = u.name.substring(13);
      const key = ('CustomField' + customFieldNumber) as CustomFieldType;
      obj[key] = {
        title: u.agencyName,
        isMultiLine: u.multiLine && !u.usePredefinedValues,
        isEditable: u.editable,
      };
    });
    return obj;
  }, [activeFields]);

  useEffect(() => {
    const getSavedState = async () => {
      setFetchingSavedState(true);
      const elementsJson = await GetCustomerSettingBySettingName(customerSettingName);
      if (elementsJson) {
        const panels = JSON.parse(elementsJson) as PanelModel[];
        setLastSavedState(panels);
        setInitialState(panels);
      }
      setFetchingSavedState(false);
      setFetchedSavedState(true);
    };
    getSavedState();
  }, []);

  useEffect(() => {
    loadingHandler && loadingHandler(fetchingSavedState || fetchingActiveFields);
  }, [loadingHandler, fetchingSavedState, fetchingActiveFields]);

  const initialStateString = useMemo(() => {
    if (initialState && initialState.length) return LayoutElementsToString(initialState);
    return '';
  }, [initialState]);

  const layoutChangeHandler = useCallback(() => {
    const api = layoutRef.current;
    if (api) {
      const currentData = api.serialize();
      const currentStringData = LayoutElementsToString(currentData);
      setHasChanges(currentStringData !== initialStateString);
    }
  }, [initialStateString]);

  useEffect(() => {
    layoutChangeHandler();
  }, [layoutSwapControl, layoutChangeHandler]);

  const elements = useMemo<CandidateRecordDashboardElementDefinition[]>(() => {
    if (!fetchedSavedState) return [];
    if (lastSavedState !== null) {
      const elements = GetPanelDefinitionsFromPanelModels(lastSavedState);
      return elements;
    }
    return defaultElements;
  }, [fetchedSavedState, lastSavedState, defaultElements]);

  const removeElementHandler = useCallback(
    (id: string) => {
      const api = layoutRef.current;
      if (api) {
        api.removePanel(id);
        layoutChangeHandler();
      }
    },
    [layoutChangeHandler]
  );

  const addElementHandler = useCallback(
    (newElementType: CandidateRecordDashboardElementType) => {
      const api = layoutRef.current;
      if (api) {
        const m = moment().format('YYYYMMDDhhmmssSS');
        const id = `${newElementType}_${m}`;
        const canGrowY =
          newElementType.startsWith('CandidateSummary') ||
          newElementType.startsWith('CandidateResume') ||
          newElementType.startsWith('CustomField');
        const [minX, minY] = GetMinSizeByElementType(newElementType);
        const [defX, defY] = GetDefaultSizeByElementType(newElementType);
        const maxSizeY = canGrowY ? undefined : minY;
        api.addPanel({ id: id, col: 0, row: 0, sizeX: 2, sizeY: 1 });
        api.updatePanel({
          id: id,
          col: 0,
          row: 0,
          sizeX: defX,
          sizeY: defY,
          minSizeX: minX,
          minSizeY: minY,
          maxSizeX: 4,
          maxSizeY: maxSizeY,
          enabled: false,
        });
        const newDataObj = api.serialize();
        setLastSavedState(newDataObj);
        setLayoutSwapControl((prev) => !prev);
        setShowAddElementDialog(false);
        layoutChangeHandler();
      }
    },
    [layoutChangeHandler]
  );

  const renderElement = useCallback(
    (id: string, type: CandidateRecordDashboardElementType) => {
      if (candidate) {
        switch (type) {
          case 'Divider':
            return (
              <Box pt="20px">
                <Divider component="div" />
              </Box>
            );
          case 'Spacer':
            return <></>;
          case 'CandidateInfo':
            return (
              <RecordAvatarComponent
                entityId={3}
                primaryText={candidate.fullName}
                secondaryText={''}
                photoUrl={candidate.photoUrl}
              />
            );
          case 'CandidateId':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="ID"
                fieldValue={candidate.id.toString()}
              />
            );
          case 'CandidateName':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Name"
                fieldValue={candidate.fullName}
              />
            );
          case 'CandidateGender':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Gender"
                fieldValue="Male"
              />
            );
          case 'CandidateAvailable':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Available"
                fieldValue={candidate.availabilityDate}
                format="date"
              />
            );
          case 'CandidateNotice': {
            const weeks = candidate.weeksNotice ? candidate.weeksNotice : 0;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Notice"
                fieldValue={weeks > 0 ? `${weeks} week(s)` : ''}
              />
            );
          }

          case 'CandidateStatus':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Status"
                fieldValue={candidate.jobSeekingStatusName}
              />
            );
          case 'CandidateDateOfBirth':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="DOB"
                fieldValue={candidate.dateOfBirth}
                format="date"
              />
            );
          case 'CandidateNationality':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Nationality"
                fieldValue={candidate.nationality}
              />
            );
          case 'CandidateDivisions':
            return (
              <SingleFieldElement disableTextSelection fieldTitle="Divisions" format="custom">
                <Chip size="small" label="Placeholder Division" sx={{ mr: '5px' }} />
              </SingleFieldElement>
            );
          case 'CandidateEmail1':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Email 1"
                fieldValue={candidate.email1}
                format="email-link"
                href={candidate.email1}
              />
            );
          case 'CandidateEmail2':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Email 2"
                fieldValue={candidate.email2}
                format="email-link"
                href={candidate.email2}
              />
            );
          case 'CandidatePayrollEmail':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Payroll Email"
                fieldValue={candidate.payrollEmail}
                format="email-link"
                href={candidate.payrollEmail}
              />
            );
          case 'CandidateWebsite': {
            const website = candidate.website;
            const href = website && !website.startsWith('http') ? 'https://' + website : website;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Website"
                href={href}
                fieldValue={website}
                format="external-link"
              />
            );
          }

          case 'CandidateLocation':
            return (
              <ViewCandidateLocationElement
                address1={candidate.address1 ?? ''}
                address2={candidate.address2 ?? ''}
                countryName={candidate.countryName}
                postcode={candidate.postcode ?? ''}
                state={candidate.state ?? ''}
                suburb={candidate.suburb ?? ''}
              />
            );
          case 'CandidateMobile': {
            const mobile = candidate.mobile_Standardised
              ? candidate.mobile_Standardised
              : candidate.mobile;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Mobile"
                fieldValue={mobile}
                href={mobile}
                format="phone-link"
              />
            );
          }

          case 'CandidatePhone': {
            const phone = candidate.home_Standardised
              ? candidate.home_Standardised
              : candidate.home;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Phone"
                fieldValue={phone}
              />
            );
          }

          case 'CandidateWork': {
            const work = candidate.work_Standardised ? candidate.work_Standardised : candidate.work;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Work"
                fieldValue={work}
              />
            );
          }

          case 'CandidateOptOut':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="OptOut"
                fieldValue={candidate.optOut ? 'Yes' : 'No'}
              />
            );
          case 'CandidateSummary':
            return (
              <MultiLineTextElement
                disableTextSelection
                title="Summary"
                content={candidate.summary}
              />
            );
          case 'CandidateSummaryFullWidth':
            return (
              <MultiLineTextElement
                disableTextSelection
                title="Summary"
                content={candidate.summary}
                dividerTitle
              />
            );
          case 'CandidatePreferredJobType':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Preferred Job Type"
                fieldValue={candidate.preferredJobTypeName}
              />
            );
          case 'CandidatePreferredRate':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Preferred Rate"
                fieldValue={candidate.preferredRate.toString()}
              />
            );
          case 'CandidatePreferredSalary':
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Preferred Salary"
                fieldValue={candidate.preferredSalary.toString()}
              />
            );
          case 'CandidateLinkedIn': {
            const linkedIn = candidate.linkedInUrl;
            const lhref =
              linkedIn && !linkedIn.startsWith('http') ? 'https://' + linkedIn : linkedIn;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="LinkedIn"
                href={lhref}
                fieldValue={linkedIn}
                format="external-link"
              />
            );
          }

          case 'CandidateTwitter': {
            const twitter = candidate.twitterUrl;
            const thref = twitter && !twitter.startsWith('http') ? 'https://' + twitter : twitter;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Twitter"
                href={thref}
                fieldValue={twitter}
                format="external-link"
              />
            );
          }

          case 'CandidateFacebook': {
            const facebook = candidate.facebookUrl;
            const fhref =
              facebook && !facebook.startsWith('http') ? 'https://' + facebook : facebook;
            return (
              <SingleFieldElement
                disableTextSelection
                useEllipsisForLongValues
                fieldTitle="Facebook"
                href={fhref}
                fieldValue={facebook}
                format="external-link"
              />
            );
          }

          case 'CandidateWorkHistorySummary':
            return <CandidateWorkHistorySummaryElement data={PlaceholderCandidateWorkHistory} />;
          case 'CandidateResume':
            return <Box bgcolor="background.default" width="100%" height="100%"></Box>;
          default:
            if (type.startsWith('CustomField')) {
              const settings = customFieldsSettingsMap[type];
              if (settings) {
                const title = settings.title;
                const { field, format } = UdfCandidateFieldMapObj[type as CustomFieldType];
                const udfValue = candidate[field];
                if (format === 'string' || format === 'date' || format === 'datetime') {
                  return (
                    <SingleFieldElement
                      disableTextSelection
                      isMultiLine={settings.isMultiLine}
                      format={format}
                      fieldTitle={title}
                      fieldValue={udfValue ? udfValue.toString() : null}
                    />
                  );
                }
                if (format === 'number') {
                  return (
                    <SingleFieldElement
                      disableTextSelection
                      format={format}
                      fieldTitle={title}
                      fieldValue={udfValue ? +udfValue : 0}
                    />
                  );
                }
              } else
                return (
                  <SingleFieldElement
                    disableTextSelection
                    fieldTitle={type}
                    fieldValue="[ Disabled Custom Field ]"
                  />
                );
            }
            return <div>{id}</div>;
        }
      }
    },
    [customFieldsSettingsMap]
  );

  const saveChangesHandler = useCallback(async () => {
    const api = layoutRef.current;
    if (api && fetchedSavedState) {
      const obj = api.serialize();
      loadingHandler && loadingHandler(true);
      const res = await UpdateCustomerSettingBySettingName(
        customerSettingName,
        JSON.stringify(obj),
        errorHandler
      );
      loadingHandler && loadingHandler(false);
      if (!res) return false;
      setInitialState(obj);
    }
    return true;
  }, [fetchedSavedState, loadingHandler, errorHandler]);

  const resetLayoutHandler = useCallback(() => {
    setLastSavedState(null);
    setShowResetToDefaultConfirmDialog(false);
    setLayoutSwapControl((prev) => !prev);
  }, []);

  const layoutResizeStopHandler = useCallback(() => {
    setScreenResizedControl((prev) => !prev);
    layoutChangeHandler();
  }, [layoutChangeHandler]);

  const renderLayout = useCallback(() => {
    if (!fetchedSavedState) return <></>;
    return (
      <DashboardLayoutComponent
        cellSpacing={cellSpacing}
        columns={columns}
        cellAspectRatio={12 / 2}
        allowResizing
        resizeStop={layoutResizeStopHandler}
        change={layoutChangeHandler}
        ref={(l) => (layoutRef.current = l)}
      >
        {elements.map((e) => (
          <PanelWrapper
            key={e.id}
            id={e.id}
            col={e.col}
            row={e.row}
            sizeX={e.sizeX}
            sizeY={e.sizeY}
            minSizeX={e.minSizeX}
            minSizeY={e.minSizeY}
            maxSizeX={e.maxSizeX}
            maxSizeY={e.maxSizeY}
            resizeControl={screenResizedControl}
            resizeIconColor={theme.palette.text.disabled}
            gapX={gapX}
            gapY={gapY}
            unitWidth={unitWidth}
            unitHeight={unitHeight}
            removeHandler={removeElementHandler}
          >
            {renderElement(e.id, e.type)}
          </PanelWrapper>
        ))}
      </DashboardLayoutComponent>
    );
  }, [
    elements,
    fetchedSavedState,
    theme.palette.text.disabled,
    screenResizedControl,
    layoutResizeStopHandler,
    layoutChangeHandler,
    removeElementHandler,
    renderElement,
  ]);

  const { unsavedChangesDialog } = useUnsavedChangesDialog(
    hasChanges,
    saveChangesHandler,
    undefined,
    undefined,
    true
  );

  const addedElementTypes = useMemo(() => {
    const api = layoutRef.current;
    if (api && showAddElementDialog) {
      const t = api.serialize();
      return t.map((e) => {
        const id = e.id;
        if (id) {
          const i = id.indexOf('_');
          return id.substring(0, i);
        }
        return '';
      });
    }
    return [];
  }, [showAddElementDialog]);

  return (
    <>
      {unsavedChangesDialog}
      <ConfirmationDialog
        message="Are you sure you want to reset the current layout to its default form? All changes will be lost"
        title="Confirm Action"
        onClose={() => setShowResetToDefaultConfirmDialog(false)}
        onContinue={resetLayoutHandler}
        open={showResetToDefaultConfirmDialog}
        fullWidth
      />
      <AddCandidateDashboardElementDialog
        open={showAddElementDialog}
        activeFields={activeFields}
        alreadyAddedElements={addedElementTypes}
        isViewRecord
        closeHandler={() => setShowAddElementDialog(false)}
        addElementHandler={addElementHandler}
      />
      <div style={{ marginBottom: '10px' }}>
        <span>
          <Button
            variant="contained"
            endIcon={<ReplayIcon />}
            onClick={() => setShowResetToDefaultConfirmDialog(true)}
            sx={{ mr: '5px' }}
          >
            Reset to default
          </Button>
          <Button
            variant="contained"
            endIcon={<AddIcon />}
            onClick={() => setShowAddElementDialog(true)}
          >
            Add Element
          </Button>
        </span>
        <Button
          color="success"
          variant="contained"
          endIcon={<SaveIcon />}
          disabled={!hasChanges}
          onClick={saveChangesHandler}
          sx={{ float: 'right' }}
        >
          Save
        </Button>
      </div>
      <Box p="10px" height="100%">
        <div className="control-section">
          {layoutSwapControl && renderLayout()}
          {!layoutSwapControl && renderLayout()}
        </div>
      </Box>
    </>
  );
}
